import { openReceiptCreationModalState, quoteItemState } from "@/store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useGomakeAxios, useGomakeRouter, useSnackBar } from "@/hooks";
import {
  printHouseHasFrontDeskFeature,
  isUsPrintHouseState,
} from "@/store/print-house-profile";
import { printHousePreferencesState } from "@/store/print-house-preferences";
import {
  createOrderApi,
  getDocumentPdfApi,
  manuallyCloseDocumentApi,
  sendQuoteApprovalToManagerApi,
} from "@/services/api-service/generic-doc/documents-api";
import {
  DOCUMENT_TYPE,
  StatusTitleText,
} from "@/pages-components/quotes/enums";
import {
  cancelReceiptApi,
  createReceiptApi,
  getReceiptInvoicesPdfApi,
  getReceiptPdfApi,
  printReceiptChecksApi,
} from "@/services/api-service/generic-doc/receipts-api";
import {
  allocatedSumsState,
  checkedItemsIdsState,
  finalTotalPaymentState,
} from "./states";
import {
  checkArrayNotEmptyOrEmailNotEmpty,
  checkArrayNotEmptyOrPhoneNotEmpty,
  isAtLeastOneSelected,
  downloadPdf,
  generateFilename,
} from "@/utils/helpers";
import { useRouter } from "next/router";
import { useUserPermission } from "@/hooks/use-permission";
import { DocumentPermission } from "@/components/CheckPermission/enum";
import {
  getAllUsersCanApproveQuoteApi,
  getEmployeeApi,
  getAllUsersApi,
} from "@/services/api-service/users/users-api";
import { currencyUnitState } from "@/store/currency-units";
import {
  buildQuoteApprovalMessage,
  buildQuoteUrl,
  formatQuoteTotalPrice,
  resolveManagerLanguage,
} from "./build-quote-approval-message";
import { newOrderIdToPrintJobsState } from "@/store/new-order-id-to-print-jobs-state";
import { Permissions } from "@/components/CheckPermission/enum";
import { EPreferences } from "@/enums/prefernces";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { useModal } from "@/hooks/use-modal";
import {
  addressModalState,
  addressTypeState,
  EAddressType,
} from "../business-widget/address-widget/state";
import { validateAddressApi } from "@/services/api-service/customers/customers-api";
import { createInvoiceFromOrderIdApi } from "@/services/api-service/generic-doc/invoices";
import {
  validateQuoteCustomerAgainstRequiredFields,
  buildQuoteDocumentAddressRequiredFieldRules,
  getMissingAddressSubFieldsByPreference,
  resolveUsesUsCountryStateAddressForm,
  normalizeQuoteDocumentAddressShape,
  isQuoteDeliveryAddressRequiredByCustomerPreferences,
  hasFilledQuoteDeliveryAddress,
} from "@/utils/customer-required-fields";
import {
  openOrderModalState,
  shouldReopenOrderModalState,
} from "@/store/order-modal-states";
import {
  checkIfCanMakeGroupedBoardMissionsUsingOrderIdApi,
  getGroupJobsUsingOrderIdApi,
  addSelectedNotesFromDuplicatedsJobApi,
} from "@/services/api-service/production-floor/production-floor-endpoints";
import {
  needsDuplicateNotesSync,
  mapSelectionToAddNotesPayload,
  readPendingQuoteConversionNotes,
  clearPendingQuoteConversionNotes,
} from "@/pages-components/board-missions/utils/duplicate-notes-selection";
import { directMessagesService } from "@/services/frontdesk/api/direct-messages.service";
import {
  StatePayment,
  sendPaymentState,
} from "@/pages-components/quotes/widgets/send-payment-modal/states";
import {
  getDocumentContactApi,
  getPrePaymentWhatsAppMessageApi,
  sendDocumentToClientForPaymentApi,
} from "@/services/api-service/pay/pay-api";
import type { PackagingLoadOptions } from "@/pages-components/packaging/use-packaging-initial-load";
import { useWhatsAppWeb } from "@/pages-components/quote-new/use-whats-app-web";
import { useDateFormat } from "@/hooks/use-date-format";
import type { IPrepaymentNotificationOptions } from "@/widgets/quote-new/total-price-and-vat/order-now-modal/types";

type UseButtonsContainerOptions = {
  getReceiptCreateIdempotencyKey?: () => string;
};

const useButtonsContainer = (
  docType: DOCUMENT_TYPE,
  options?: UseButtonsContainerOptions,
) => {
  const { navigate } = useGomakeRouter();
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const router = useRouter();
  const { push } = useRouter();
  const { CheckDocumentPermission, CheckPermission } = useUserPermission();
  const { CheckPreferenceValue } = usePrintHousePreferences();
  const hasFrontDeskFeature = useRecoilValue(printHouseHasFrontDeskFeature);
  const currenciesUnits = useRecoilValue(currencyUnitState);
  const { openWhatsAppWebLink } = useWhatsAppWeb();
  const { formatISODateTime } = useDateFormat();
  const isUsPrintHouse = useRecoilValue(isUsPrintHouseState);
  const usesUsCountryStateAddressForm = resolveUsesUsCountryStateAddressForm(
    isUsPrintHouse,
    !!CheckPreferenceValue(EPreferences.ADDRESSES_WITH_COUNTRY_AND_STATE),
  );
  const printHousePrefs = useRecoilValue(printHousePreferencesState);
  const setOpenAddressModal = useSetRecoilState<boolean>(addressModalState);
  const setSelectedAddressType =
    useSetRecoilState<EAddressType>(addressTypeState);

  const [quoteItemValue, setQuoteItemValue] =
    useRecoilState<any>(quoteItemState);
  const {
    alertFault,
    alertFaultSend,
    alertSuccessDelete,
    alertFaultDelete,
    alertSuccessUpdate,
    alertFaultUpdate,
    alertFaultAdded,
    alertSuccessAdded,
    alertFaultGetData,
  } = useSnackBar();
  const AlertAddCustomerModal = useModal();
  const ReceiptSummeryModal = useModal();

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openOrderNowModal, setOpenOrderNowModal] =
    useRecoilState<boolean>(openOrderModalState);

  const CheckoutOptionModal = useModal();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCancelReceiptModal, setOpenCancelReceiptModal] = useState(false);
  const [isSelectedAtLeastOne, setIsSelectedAtLeastOne] = useState(null);
  const [openMangerApprovalModal, setOpenMangerApprovalModal] = useState(false);
  const [, setShowPrintJobsModal] = useRecoilState(newOrderIdToPrintJobsState);
  const [openReceiptCreationModal, setOpenReceiptCreationModal] =
    useRecoilState(openReceiptCreationModalState);

  const [openOrderNowPrePaymentModal, setOpenOrderNowPrePaymentModal] =
    useState(false);

  const onClickCloseOrderNowPreModal = () => {
    setOpenOrderNowPrePaymentModal(false);
  };

  const onClickOpenReceiptCreation = () => {
    setOpenReceiptCreationModal(true);
  };
  const onClickCloseReceiptCreation = () => {
    setOpenReceiptCreationModal(false);
  };

  useEffect(() => {
    const data = isAtLeastOneSelected(quoteItemValue?.documentItems);
    setIsSelectedAtLeastOne(data);
  }, [quoteItemValue]);

  const finalTotalPayment = useRecoilValue<number>(finalTotalPaymentState);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>();
  const open = Boolean(anchorEl);

  const validateAddress = async (addressWithMetadata) => {
    return await validateAddressApi(callApi, () => {}, addressWithMetadata);
  };

  const setShouldReopen = useSetRecoilState(shouldReopenOrderModalState);

  const onClickOpenOrderNowModal = async (
    addressChecked: boolean = false,
    quoteItemOverride?: any,
  ) => {
    const activeQuoteItem = quoteItemOverride ?? quoteItemValue;

    if (!activeQuoteItem?.isHaveCustomers && !activeQuoteItem?.client) {
      AlertAddCustomerModal.open();
      return;
    }

    // Check if client is present and can create orders
    if (!activeQuoteItem?.client?.isCreateOrder) {
      alertFault("home.admin.pleaseSelectClient");
      return;
    }

    const isAddressRequired = CheckPreferenceValue(
      EPreferences.ADDRESSES_REQUIRED_FOR_ORDER,
    );
    const isDeliveryAddressRequired =
      isQuoteDeliveryAddressRequiredByCustomerPreferences(
        printHousePrefs ?? [],
        CheckPreferenceValue(
          EPreferences.CHECK_CUSTOMER_REQUIRED_FIELDS_IN_QUOTE,
        ),
      );
    const noDeliveryAddress = !hasFilledQuoteDeliveryAddress(activeQuoteItem);
    if (isDeliveryAddressRequired && noDeliveryAddress) {
      setSelectedAddressType(EAddressType.DELIVERY);
      setShouldReopen(true);
      setTimeout(() => {
        setOpenAddressModal(true);
      }, 0);
      return;
    }
    const noBillingAddress =
      (activeQuoteItem?.documentBillingAddresses?.length ?? 0) === 0;

    if (isAddressRequired && noBillingAddress && !addressChecked) {
      setSelectedAddressType(EAddressType.BILLING);
      setShouldReopen(true);
      setTimeout(() => {
        setOpenAddressModal(true);
      }, 0);
      return;
    }

    if (!isSelectedAtLeastOne) {
      alertFault("reports.pleaseSelectOneItem");
      return;
    }

    const itemsRequiringEditing = [];
    activeQuoteItem?.documentItems?.forEach((item, index) => {
      if (item?.isRequiredEditing) {
        itemsRequiringEditing.push(index + 1);
      }
    });

    if (itemsRequiringEditing.length > 0) {
      const itemNumbers = itemsRequiringEditing.join(", ");
      alertFault(
        t("alerts.itemsMustBeEdited", {
          itemNumbers: itemNumbers,
        }),
      );
      return;
    }

    const hasValidPhone = checkArrayNotEmptyOrPhoneNotEmpty(
      activeQuoteItem?.documentContacts || [],
    );
    const hasValidEmail = checkArrayNotEmptyOrEmailNotEmpty(
      activeQuoteItem?.documentContacts || [],
    );

    // Ensure phone or email is provided
    if (!hasValidPhone && !hasValidEmail) {
      alertFault("home.admin.pleaseSelectPhoneOrEmail");
      return;
    }

    // Open appropriate modal
    // Check if payment is required: either client is prepayment OR isPaymentRequired flag is set
    const isPaymentRequired =
      activeQuoteItem?.isPrepaymentClient || activeQuoteItem?.isPaymentRequired;
    if (isPaymentRequired) {
      setOpenOrderNowPrePaymentModal(true);
    } else {
      setOpenOrderNowModal(true);
    }
  };

  const onClickCloseOrderNowModal = () => {
    setOpenOrderNowModal(false);
  };

  const onClickOpenPaymentModal = (selectedTabIndex) => {
    setSelectedTabIndex(selectedTabIndex);
    setOpenPaymentModal(true);
  };

  const onClickClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const onClickOpenDeleteModal = () => {
    setOpenDeleteModal(true);
  };

  const onClickCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const onClickOpenCancelReceiptModal = () => {
    setOpenCancelReceiptModal(true);
  };

  const onClickCloseCancelReceiptModal = () => {
    setOpenCancelReceiptModal(false);
  };

  const CheckoutModalOpen = useModal();
  const [checkoutModalData, setCheckoutModalData] = useState<{
    orderId: string;
    orderNumber: string;
  } | null>(null);
  const [pendingOrderData, setPendingOrderData] = useState(null);

  const navigateToJobsAfterOrderCreated = (
    orderId: string,
    orderNumber: string,
    shouldOpenComposer: boolean = false,
  ) => {
    setShowPrintJobsModal(orderId);
    if (!CheckPermission(Permissions.SHOW_BOARD_MISSIONS)) {
      navigate("/");
      return;
    }
    const queryParams = shouldOpenComposer
      ? `orderNumber=${orderNumber}&openComposer=true&orderId=${orderId}`
      : `orderNumber=${orderNumber}`;
    navigate(`/jobs?${queryParams}`);
  };

  const sendBodyCommingFromOrder = (
    res,
    sendMessage,
    isPrepaymentClient,
    _isStripeCash,
    shouldOpenComposer = false,
  ) => {
    if (isPrepaymentClient) {
      // When shouldOpenComposer=true, always open composer (same as jobs page behavior)
      if (shouldOpenComposer) {
        // Extract client information from quote to pass in URL
        const firstContact = quoteItemValue?.documentContacts?.[0];
        const contactEmail =
          firstContact?.contactMail ||
          firstContact?.email ||
          quoteItemValue?.contactEmail ||
          quoteItemValue?.contactMail;
        const contactPhone =
          firstContact?.contactPhone ||
          firstContact?.phone ||
          quoteItemValue?.contactPhone;
        const contactName =
          firstContact?.contactName ||
          firstContact?.name ||
          quoteItemValue?.contactName;
        const customerId =
          quoteItemValue?.customerID ||
          quoteItemValue?.clientId ||
          quoteItemValue?.client?.id;
        const customerName =
          quoteItemValue?.customerName ||
          quoteItemValue?.clientName ||
          quoteItemValue?.client?.name;

        // Build URL parameters
        const paymentRequestIdParam = res?.data?.paymentRequestId
          ? `&paymentRequestId=${encodeURIComponent(
              res?.data?.paymentRequestId,
            )}`
          : "";
        const contactEmailParam = contactEmail
          ? `&contactEmail=${encodeURIComponent(contactEmail)}`
          : "";
        const contactPhoneParam = contactPhone
          ? `&contactPhone=${encodeURIComponent(contactPhone)}`
          : "";
        const contactNameParam = contactName
          ? `&contactName=${encodeURIComponent(contactName)}`
          : "";
        const customerIdParam = customerId
          ? `&customerId=${encodeURIComponent(customerId)}`
          : "";
        const customerNameParam = customerName
          ? `&customerName=${encodeURIComponent(customerName)}`
          : "";

        navigate(
          `/orders?finalPatternSearch=${res?.data?.number}&openComposer=true&orderId=${res?.data?.orderID}${paymentRequestIdParam}&isPrepaymentClient=${isPrepaymentClient}${contactEmailParam}${contactPhoneParam}${contactNameParam}${customerIdParam}${customerNameParam}`,
        );
      } else {
        navigate(`/orders?finalPatternSearch=${res?.data?.number}`);
      }
    } else {
      if (res.data.isShowCheckoutModal) {
        setCheckoutModalData({
          orderId: res.data.orderID,
          orderNumber: res.data.number,
        });

        CheckoutModalOpen.open();
      } else {
        navigateToJobsAfterOrderCreated(
          res.data.orderID,
          res?.data?.number,
          shouldOpenComposer,
        );
      }
    }
  };

  const handleCheckIfCanMakeGroupedBoardMissionsUsingOrderId = async (
    orderId,
    res,
    sendMessage,
    isPrepaymentClient,
    isStripeCash,
    shouldOpenComposer = false,
  ) => {
    const callBack = (result) => {
      if (
        result?.success &&
        CheckPreferenceValue(EPreferences.ASK_TO_GROUP_SIMILAR_JOBS)
      ) {
        if (result?.data?.item1) {
          setPendingOrderData({
            orderId: orderId,
            res: res,
            sendMessage: sendMessage,
            isPrepaymentClient: isPrepaymentClient,
            isStripeCash: isStripeCash,
            shouldOpenComposer: shouldOpenComposer,
          });
          isOpenGroupJobsModal.open();
        } else {
          sendBodyCommingFromOrder(
            res,
            sendMessage,
            isPrepaymentClient,
            isStripeCash,
            shouldOpenComposer,
          );
        }
      } else {
        sendBodyCommingFromOrder(
          res,
          sendMessage,
          isPrepaymentClient,
          isStripeCash,
          shouldOpenComposer,
        );
      }
    };

    await checkIfCanMakeGroupedBoardMissionsUsingOrderIdApi(
      callApi,
      callBack,
      orderId,
    );
  };

  const handleGetGroupJobsUsingOrderId = async (pendingOrderData) => {
    const callBack = (result) => {
      if (result?.success) {
        isOpenGroupJobsModal.close();
        sendBodyCommingFromOrder(
          pendingOrderData?.res,
          pendingOrderData?.sendMessage,
          pendingOrderData?.isPrepaymentClient,
          pendingOrderData?.isStripeCash,
          pendingOrderData?.shouldOpenComposer,
        );
      } else {
        isOpenGroupJobsModal.close();
        setPendingOrderData(null);
        alertFaultUpdate();
      }
    };

    await getGroupJobsUsingOrderIdApi(
      callApi,
      callBack,
      pendingOrderData?.orderId,
    );
  };
  const isOpenGroupJobsModal = useModal();

  const handlePrePaymentWhatsApp = (
    orderId: string,
    totalPrePaymentAmount: number,
    percentageOfTotal: number,
    prepaymentNotification: IPrepaymentNotificationOptions,
    whatsappPopupWindow?: Window | null,
    paymentRequestId?: string,
  ) => {
    const dataToSend = {
      documentId: orderId,
      price: totalPrePaymentAmount,
      maxPayments: 1,
      invoiceDateStr: formatISODateTime(new Date()).split(" ")[0],
      contactName: prepaymentNotification.contactName,
      contactPhone: prepaymentNotification.contactPhone,
      contactMail: prepaymentNotification.contactMail,
      sendByMail: false,
      sendBySms: false,
      sendByWhatsapp: true,
      percentageOfTotal,
      ...(paymentRequestId ? { paymentRequestId } : {}),
    };

    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 700;

    const attemptFetch = (attempt: number) => {
      getPrePaymentWhatsAppMessageApi(
        callApi,
        (res) => {
          if (res?.success && res?.data) {
            openWhatsAppWebLink(
              res.data,
              prepaymentNotification.contactPhone,
              true,
              whatsappPopupWindow,
            );
            return;
          }

          if (attempt < MAX_ATTEMPTS - 1) {
            setTimeout(() => attemptFetch(attempt + 1), RETRY_DELAY_MS);
            return;
          }

          if (whatsappPopupWindow && !whatsappPopupWindow.closed) {
            whatsappPopupWindow.close();
          }
          alertFaultSend();
        },
        {
          documentType: DOCUMENT_TYPE.order,
          data: dataToSend,
        },
      );
    };

    // Brief delay so ERP can finish creating the prepayment record after convert-quote-to-order
    setTimeout(() => attemptFetch(0), 300);
  };

  const handleOrderConfirmation = async (
    sendMessage: boolean,
    isPrepaymentClient: boolean = false,
    totalPrePaymentAmount: number = 0,
    percentageOfTotal: number = 0,
    isStripeCash: boolean = false,
    shouldOpenComposer: boolean = false,
    prepaymentNotification?: IPrepaymentNotificationOptions,
    whatsappPopupWindow?: Window | null,
  ) => {
    if (
      docType === DOCUMENT_TYPE.quote &&
      CheckPreferenceValue(EPreferences.CHECK_CUSTOMER_REQUIRED_FIELDS_IN_QUOTE)
    ) {
      const { isValid, missingFields } =
        validateQuoteCustomerAgainstRequiredFields(
          quoteItemValue,
          printHousePrefs ?? [],
          {
            addressFormIsUs: usesUsCountryStateAddressForm,
            showCpaCode: CheckPermission(Permissions.SHOW_CPA_CODE),
          },
        );

      if (!isValid) {
        alertFault(
          `${t("modal.RequiredFields")}: ${missingFields
            .map((field) => t(field))
            .join(", ")}`,
        );
        return;
      }
    }

    const apiSendMessage = prepaymentNotification
      ? prepaymentNotification.sendByMail ||
        prepaymentNotification.sendBySms ||
        prepaymentNotification.sendByWhatsapp
      : sendMessage;

    const callBack = async (res) => {
      if (res?.success) {
        const pendingDuplicateNotes = readPendingQuoteConversionNotes(
          quoteItemValue?.id,
        );
        const boardMissionId = res?.data?.jobsIds?.[0];

        // Also runs for ignoreAll (empty notes) so Book section notes without
        // printingActionId are cleared after quote → order.
        // Scoped to this quote id so another convert cannot inherit the pending.
        if (needsDuplicateNotesSync(pendingDuplicateNotes) && boardMissionId) {
          await addSelectedNotesFromDuplicatedsJobApi(callApi, () => {}, {
            boardMissionId,
            productType: pendingDuplicateNotes.productType ?? "",
            notes: mapSelectionToAddNotesPayload(pendingDuplicateNotes),
          });
          clearPendingQuoteConversionNotes();
        }

        alertSuccessUpdate();
        onClickCloseOrderNowModal();
        if (isPrepaymentClient) {
          onClickCloseOrderNowPreModal();
        }

        if (
          isPrepaymentClient &&
          prepaymentNotification?.sendByWhatsapp &&
          !hasFrontDeskFeature &&
          whatsappPopupWindow
        ) {
          handlePrePaymentWhatsApp(
            res?.data?.orderID,
            totalPrePaymentAmount,
            percentageOfTotal,
            prepaymentNotification,
            whatsappPopupWindow,
            res?.data?.paymentRequestId,
          );
        }

        handleCheckIfCanMakeGroupedBoardMissionsUsingOrderId(
          res?.data?.orderID,
          res,
          apiSendMessage,
          isPrepaymentClient,
          isStripeCash,
          shouldOpenComposer,
        );
      } else {
        if (whatsappPopupWindow && !whatsappPopupWindow.closed) {
          whatsappPopupWindow.close();
        }
        alertFaultUpdate();
      }
    };

    const requestBody = {
      quoteId: quoteItemValue?.id,
      sendMessage: apiSendMessage,
      ...(isPrepaymentClient && { totalPrePaymentAmount }),
      ...(isPrepaymentClient && { percentageOfTotal }),
      ...(isPrepaymentClient &&
        prepaymentNotification && {
          sendByMail: hasFrontDeskFeature
            ? false
            : prepaymentNotification.sendByMail,
          sendBySms: hasFrontDeskFeature
            ? false
            : prepaymentNotification.sendBySms,
          sendByWhatsapp: hasFrontDeskFeature
            ? false
            : prepaymentNotification.sendByWhatsapp,
          contactPhone: prepaymentNotification.contactPhone,
          contactMail: prepaymentNotification.contactMail,
          contactName: prepaymentNotification.contactName,
        }),
    };

    await createOrderApi(callApi, callBack, requestBody);
  };

  const handleProcessCheckout = async (orderId: string) => {
    const callBack = (res) => {
      if (res?.success) {
        const DocumentNumber = res.data.docEntry;
        const ClientId = res.data.client.id;
        navigate(
          `/receipt?isNewCreation=true&documentNumber=${DocumentNumber}&ClientId=${ClientId}`,
        );
        CheckoutModalOpen.close();
      }
    };

    await createInvoiceFromOrderIdApi(callApi, callBack, orderId);
  };

  const onClickConfirmWithoutNotification = () =>
    handleOrderConfirmation(false, false, 0, 0, false, hasFrontDeskFeature);
  const onClickConfirmWithNotification = () =>
    handleOrderConfirmation(true, false, 0, 0, false, hasFrontDeskFeature);
  const onClickConfirm = () => {
    // When frontdesk is enabled, always open composer after order creation
    handleOrderConfirmation(false, false, 0, 0, false, hasFrontDeskFeature);
  };

  const onClickPrint = async (withInvoices?: boolean) => {
    const callBack = (res) => {
      if (res?.success) {
        const pdfLink = res.data;
        let filename: string;

        if (isReceipt) {
          if (withInvoices) {
            filename = generateFilename(
              "Receipt",
              quoteItemValue?.id,
              undefined,
              "pdf",
            );
          } else {
            filename = generateFilename(
              "Receipt",
              quoteItemValue?.id,
              undefined,
              "pdf",
            );
          }
        } else {
          const docTypeName = DOCUMENT_TYPE[docType] || "Document";
          filename = generateFilename(
            docTypeName,
            quoteItemValue?.id,
            undefined,
            "pdf",
          );
        }

        // Open in new tab for viewing (user can still download from browser)
        downloadPdf(pdfLink, filename, true);
      } else {
        alertFaultGetData();
      }
    };

    if (isReceipt) {
      if (withInvoices)
        await getReceiptInvoicesPdfApi(callApi, callBack, {
          receiptId: quoteItemValue?.id,
        });
      else
        await getReceiptPdfApi(callApi, callBack, {
          receiptId: quoteItemValue?.id,
        });
    } else {
      await getDocumentPdfApi(callApi, callBack, {
        documentId: quoteItemValue?.id,
        documentType: docType,
      });
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getFormattedDocumentPath = (docType: DOCUMENT_TYPE): string => {
    const documentPath = isOutgoingPayments
      ? "supplierPayment"
      : DOCUMENT_TYPE[docType];
    return documentPath.charAt(0).toUpperCase() + documentPath.slice(1);
  };

  const checkedItemsIds = useRecoilValue(checkedItemsIdsState);

  const allocatedSums = useRecoilValue(allocatedSumsState);

  const filteredReceiptItems = Object.keys(checkedItemsIds)
    .map((key) => {
      const item = quoteItemValue?.receiptItems?.find(
        (item) => item.id === checkedItemsIds[key],
      );
      const sumApplied = allocatedSums[key] || 0;
      return item ? { ...item, sumApplied } : null;
    })
    .filter(Boolean);

  const onClickCreateNewReceipt = async () => {
    const newReceiptItem = {
      ...quoteItemValue,
      receiptItems: filteredReceiptItems,
    };
    if (finalTotalPayment === 0) {
      alertFault(t("alerts.totalPaymentZero"));
      return;
    }
    // Check if receipt items are empty and user doesn't have permission to create empty receipts
    if (
      filteredReceiptItems.length === 0 &&
      !CheckPermission(Permissions.CAN_CREATE_EMPTY_RECEIPT)
    ) {
      alertFault(
        t("alerts.receiptItemsRequired") ||
          "Please select at least one invoice or document to create a receipt",
      );
      return;
    }
    const callBack = (res) => {
      if (res?.success) {
        const receiptId = res?.data;
        alertSuccessAdded();
        isOutgoingPayments
          ? router.replace(`/receipt?isOutgoingPayments=true&Id=${receiptId}`)
          : router.replace(`/receipt?Id=${receiptId}`);
        onClickOpenReceiptCreation();
      } else {
        alertFaultAdded();
      }
    };
    const receiptIdempotencyKey =
      docType === DOCUMENT_TYPE.receipt &&
      router.query.isNewCreation === "true" &&
      options?.getReceiptCreateIdempotencyKey
        ? options.getReceiptCreateIdempotencyKey()
        : undefined;
    await createReceiptApi(callApi, callBack, {
      newReceiptItem: {
        ...newReceiptItem,
        ...(receiptIdempotencyKey
          ? { idempotencyKey: receiptIdempotencyKey }
          : {}),
      },
    });
  };

  const handleOpenReceiptSummary = () => {
    if (!quoteItemValue?.client?.id) {
      alertFault(t("home.admin.pleaseSelectClient"));
      return;
    }

    if (finalTotalPayment === 0) {
      alertFault(t("alerts.totalPaymentZero"));
      return;
    }

    // Check if receipt items are empty and user doesn't have permission to create empty receipts
    if (
      filteredReceiptItems.length === 0 &&
      !CheckPermission(Permissions.CAN_CREATE_EMPTY_RECEIPT)
    ) {
      alertFault(
        t("alerts.receiptItemsRequired") ||
          "Please select at least one invoice or document to create a receipt",
      );
      return;
    }

    ReceiptSummeryModal.open();
  };

  const onClickCancelReceipt = async (refundCredit: boolean) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessDelete();
        isOutgoingPayments
          ? navigate("/supplier-payments")
          : navigate("/receipts");
      } else {
        alertFaultDelete();
      }
    };
    await cancelReceiptApi(callApi, callBack, {
      id: quoteItemValue?.id,
      refundCredit,
    });
  };

  const isNewCreation = router?.query?.isNewCreation;
  const isInvoice = docType === DOCUMENT_TYPE.invoice;
  const isDeliveryNote = docType === DOCUMENT_TYPE.deliveryNote;
  const isProformaInvoice = docType === DOCUMENT_TYPE.proformaInvoice;
  const isQuote = docType === DOCUMENT_TYPE.quote;
  const isOrder = docType === DOCUMENT_TYPE.order;
  const isOutgoingPayments = router.query.isOutgoingPayments === "true";
  const isReceipt = docType === DOCUMENT_TYPE.receipt;
  const isPurchaseInvoice = docType === DOCUMENT_TYPE.purchaseInvoice;
  const isPurchaseOrder = docType === DOCUMENT_TYPE.purchaseOrder;

  const canCancelReceipt = CheckPermission(Permissions.CANCEL_RECEIPT);

  const canEditDocument =
    quoteItemValue?.isEditable &&
    CheckDocumentPermission(
      docType.toString(),
      DocumentPermission.EDIT_DOCUMENT,
    );
  // Add delivery: quote / order (any time the document exists) and invoice /
  // delivery note (only while creating; once saved, delivery is handled by the
  // Pack & Ship button).
  const canAddDelivery =
    docType === DOCUMENT_TYPE.quote ||
    docType === DOCUMENT_TYPE.order ||
    docType === DOCUMENT_TYPE.invoice ||
    docType === DOCUMENT_TYPE.deliveryNote;

  const showAddNewItemBtn =
    canEditDocument && docType !== DOCUMENT_TYPE.receipt;

  const onClickCloseMangerApprovalModal = () => {
    setOpenMangerApprovalModal(false);
  };

  const onClickOpenMangerApprovalModal = () => {
    setOpenMangerApprovalModal(true);
  };

  const [managersOptions, setManagersOptions] = useState([]);
  const getAllUsersCanApproveQuote = async () => {
    const callBack = (res) => {
      if (res.success) {
        const mappedManagers = res.data.map((manager) => ({
          value: manager.id,
          text: manager.name,
          email: manager.email, // Include email from API response
        }));
        setManagersOptions(mappedManagers);
      }
    };
    await getAllUsersCanApproveQuoteApi(callApi, callBack);
  };

  const onClickSendAndChat = async (managerIds: string[]) => {
    // Send FrontDesk messages to managers before external API
    for (const managerId of managerIds) {
      try {
        // Get employee details by employeeId (manager.id)
        const employeeCallBack = async (employeeRes: any) => {
          if (employeeRes.success) {
            // Use employeeRes.data.id as participant for this specific manager
            const participantId = employeeRes.data.id;
            try {
              const managerLang = resolveManagerLanguage(employeeRes.data);
              const quoteId = quoteItemValue?.id || "";
              const quoteNumber = String(
                quoteItemValue?.number || quoteItemValue?.documentNumber || "N/A",
              );
              const clientName = String(
                quoteItemValue?.client?.name ||
                  quoteItemValue?.clientName ||
                  quoteItemValue?.customerName ||
                  "N/A",
              );
              const totalPrice = formatQuoteTotalPrice(
                quoteItemValue || {},
                currenciesUnits,
              );
              const quoteUrl = buildQuoteUrl(quoteId);
              const approvalMessage = buildQuoteApprovalMessage({
                managerLang,
                quoteId,
                quoteNumber,
                clientName,
                totalPrice,
                quoteUrl,
              });

              // Create separate conversation for each manager
              const { conversation } =
                await directMessagesService.createConversation({
                  participantIds: [participantId],
                  name: approvalMessage.conversationName,
                  contextId: quoteId,
                  contextType: "QUOTE",
                });

              await directMessagesService.sendMessage(conversation.id, {
                content: approvalMessage.content,
                metadata: approvalMessage.metadata,
              });
            } catch (messageError) {
              console.error(
                `Failed to send message to participant ${participantId}`,
                messageError,
              );
            }
          } else {
            console.warn(
              `Could not get employee details for employeeId: ${managerId}`,
            );
          }
        };
        await getEmployeeApi(callApi, employeeCallBack, managerId);
      } catch (error) {
        console.error(
          `Failed to send FrontDesk message to manager ${managerId}:`,
          error,
        );
        // Continue anyway - don't block the main approval flow
      }
    }

    // Continue with existing external API call
    const callBack = (res) => {
      if (res?.success) {
        if (res?.data) {
          push(`/messenger?dialogId=${res.data}`);
        } else {
          push(`/quotes`);
        }
      } else alertFaultSend();
    };
    await sendQuoteApprovalToManagerApi(callApi, callBack, {
      docType,
      data: { managerIds, documentId: quoteItemValue?.id },
    });
  };

  const [anchorElCloseBtn, setAnchorElCloseBtn] = useState<null | HTMLElement>(
    null,
  );
  const openCloseBtn = Boolean(anchorElCloseBtn);

  const handleCloseBtnClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElCloseBtn(event.currentTarget);
  };

  const handleCloseBtnClose = () => {
    setAnchorElCloseBtn(null);
  };

  const isShowCloseButton =
    quoteItemValue?.isCanClose &&
    !quoteItemValue?.isLocked &&
    ((isOrder &&
      quoteItemValue?.statusTitleText !== StatusTitleText.OrderCanceled) ||
      isInvoice ||
      isDeliveryNote ||
      isPurchaseOrder ||
      isPurchaseInvoice);

  const ManuallyCloseDocument = async (selectedDocumentId) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        isOrder
          ? navigate("/orders")
          : isDeliveryNote
            ? navigate("/deliveryNotes")
            : isInvoice
              ? navigate("/invoices")
              : navigate("/purchaseOrders");
      } else {
        alertFaultUpdate();
      }
    };
    await manuallyCloseDocumentApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: selectedDocumentId,
        documentNumber: documentCloseNumber,
      },
    });
  };

  const [selectedDocumentId, setSelectedDocumentId] = useState<any>({});
  const [openCloseOrderModal, setOpenCloseOrderModal] = useState(false);
  const [documentCloseNumber, setDocumentCloseNumber] = useState("");

  const onClickOpenCloseOrderModal = (documentId) => {
    setSelectedDocumentId(documentId);
    setOpenCloseOrderModal(true);
  };

  const onClickCloseCloseOrderModal = () => {
    setOpenCloseOrderModal(false);
    setDocumentCloseNumber("");
  };

  const handleDocumentNumberChange = (event) => {
    setDocumentCloseNumber(event.target.value);
  };

  const handleAddExistingItem = () => {
    if (isQuote) {
      navigate(`/jobs?clientId=${quoteItemValue?.customerID}`);
    } else if (isOrder) {
      navigate(
        `/jobs?targetOrderId=${quoteItemValue?.id}&clientId=${quoteItemValue?.customerID}&clientName=${quoteItemValue?.client?.name}`,
      );
    }
  };

  const [openExportReceiptPdfModal, setOpenExportReceiptPdfModal] =
    useState<boolean>(false);

  const onOpenExportReceiptPdfModal = () => {
    setOpenExportReceiptPdfModal(true);
  };

  const onCloseExportReceiptPdfModal = () => {
    setOpenExportReceiptPdfModal(false);
  };

  const handleClickPrint = () => {
    if (isReceipt) {
      onOpenExportReceiptPdfModal();
    } else onClickPrint();
  };

  const handleClickPrintChecks = async () => {
    const callBack = (res) => {
      if (res?.success) {
        const pdfLink = res.data;
        const filename = generateFilename(
          "Receipt_Checks",
          quoteItemValue?.id,
          undefined,
          "pdf",
        );
        downloadPdf(pdfLink, filename);
      } else {
        alertFaultGetData();
      }
    };

    await printReceiptChecksApi(callApi, callBack, {
      receiptId: quoteItemValue?.id,
    });
  };

  const [openCloseOrderNotesModal, setOpenCloseOrderNotesModal] =
    useState(false);
  const [onClickCloseOrderCallback, setOnClickCloseOrderCallback] = useState<
    (() => void) | null
  >(null);

  const onClickOpenCloseOrderNotesModal = (onClickCloseOrder: () => void) => {
    setOnClickCloseOrderCallback(() => onClickCloseOrder);
    setOpenCloseOrderNotesModal(true);
  };

  const onClickCloseCloseOrderNotesModal = () => {
    setOpenCloseOrderNotesModal(false);
  };

  // Send Payment Modal State
  const [openSendPaymentModal, setOpenSendPaymentModal] = useState(false);
  const [isSendToClient, setIsSendToClient] = useState(false);
  const [isSendOrderReadyToClient, setIsSendOrderReadyToClient] =
    useState(false);
  const [paymentModalTitle, setPaymentModalTitle] = useState("");
  const [sendPayment, setSendPayment] =
    useRecoilState<StatePayment>(sendPaymentState);
  const [openAddProductWithAIModal, setOpenAddProductWithAIModal] =
    useState(false);
  const [openPackagingModal, setOpenPackagingModal] = useState(false);
  const [openShippingModal, setOpenShippingModal] = useState(false);

  const onClickCloseSendPaymentModal = () => {
    setOpenSendPaymentModal(false);
  };

  const getSingleValue = (value: string | string[] | undefined) =>
    Array.isArray(value) ? value[0] : value;

  const getDocumentFlowContext = (): PackagingLoadOptions => {
    let sourceDocumentType: PackagingLoadOptions["sourceDocumentType"];
    if (isOrder) {
      sourceDocumentType = "order";
    } else if (isQuote) {
      sourceDocumentType = "quote";
    } else if (isInvoice) {
      sourceDocumentType = "invoice";
    } else if (isDeliveryNote) {
      sourceDocumentType = "deliveryNote";
    } else {
      sourceDocumentType = undefined;
    }
    const sourceDocumentId = quoteItemValue?.id
      ? String(quoteItemValue.id)
      : undefined;
    const sourceDocumentNumber = quoteItemValue?.number
      ? String(quoteItemValue.number)
      : undefined;
    const existingOrderId =
      (isOrder ? quoteItemValue?.id : undefined) ||
      quoteItemValue?.orderId ||
      quoteItemValue?.orderID ||
      quoteItemValue?.orderIdForDeliveryCreation ||
      getSingleValue(router.query.orderId);

    return {
      sourceDocumentType,
      sourceDocumentId,
      sourceDocumentNumber,
      orderId: existingOrderId ? String(existingOrderId) : undefined,
      quoteId:
        isQuote && quoteItemValue?.id ? String(quoteItemValue.id) : undefined,
    };
  };

  const buildDocumentFlowRoute = (flowType: "packaging" | "shipping") => {
    const params = new URLSearchParams();
    const {
      sourceDocumentType,
      sourceDocumentId,
      sourceDocumentNumber,
      orderId,
      quoteId,
    } = getDocumentFlowContext();

    if (orderId) {
      params.set("orderId", orderId);
    }
    if (quoteId) {
      params.set("quoteId", quoteId);
    }
    if (sourceDocumentType) {
      params.set("sourceDocumentType", sourceDocumentType);
    }
    if (sourceDocumentId) {
      params.set("sourceDocumentId", sourceDocumentId);
    }
    if (sourceDocumentNumber) {
      params.set("sourceDocumentNumber", sourceDocumentNumber);
    }

    const queryString = params.toString();
    return `/${flowType}${queryString ? `?${queryString}` : ""}`;
  };

  const onClickOpenPackagingModal = () => {
    if (!quoteItemValue?.client?.id) {
      alertFault(t("home.admin.pleaseSelectClient"));
      return;
    }

    setOpenPackagingModal(true);
  };

  const onClickClosePackagingModal = () => {
    setOpenPackagingModal(false);
  };

  const onClickOpenShippingModal = () => {
    setOpenShippingModal(true);
  };

  const onClickCloseShippingModal = () => {
    setOpenShippingModal(false);
  };

  const navigateToPackagingPage = () => {
    navigate(buildDocumentFlowRoute("packaging"));
    onClickClosePackagingModal();
  };

  const navigateToShippingPage = () => {
    navigate(buildDocumentFlowRoute("shipping"));
    onClickCloseShippingModal();
  };

  const onClickSendPaymentOld = (
    document: any,
    isSendToClientParam: boolean,
  ): Promise<void> => {
    if (isSendToClientParam) {
      return new Promise<void>((resolve, reject) => {
        const callBack = (res) => {
          if (res?.success) {
            const newSendPaymentState = { ...sendPayment };
            newSendPaymentState.documentId = document?.id;
            newSendPaymentState.documentContacts = res.data;
            setSendPayment(newSendPaymentState);
            resolve();
          } else {
            reject(new Error("Failed to update payment state"));
          }
        };
        getDocumentContactApi(callApi, callBack, {
          documentId: document?.id,
          documentType: docType,
        });
      });
    } else {
      return new Promise<void>((resolve, reject) => {
        const callBack = (res) => {
          if (res?.success) {
            const newSendPaymentState = { ...sendPayment, ...res.data };
            setSendPayment(newSendPaymentState);
            resolve();
          } else {
            reject(new Error("Failed to update payment state"));
          }
        };
        sendDocumentToClientForPaymentApi(callApi, callBack, {
          id: document?.id,
          documentType: docType,
        });
      });
    }
  };

  const onClickSendPayment = async (
    document: any,
    isSendToClientParam: boolean,
    isSendOrderReadyToClientParam: boolean = false,
  ) => {
    setIsSendToClient(isSendToClientParam);
    setIsSendOrderReadyToClient(isSendOrderReadyToClientParam);
    try {
      await onClickSendPaymentOld(document, isSendToClientParam);
      setPaymentModalTitle(
        `${t("prePayment.paymentPageFor")} ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setOpenSendPaymentModal(true);
    } catch (error) {
      alertFaultGetData();
    }
  };

  return {
    openOrderNowModal,
    onClickConfirmWithoutNotification,
    onClickConfirmWithNotification,
    onClickConfirm,
    onClickOpenOrderNowModal,
    onClickCloseOrderNowModal,
    onClickPrint,
    t,
    onClickClosePaymentModal,
    onClickOpenPaymentModal,
    openPaymentModal,
    selectedTabIndex,
    quoteItemValue,
    alertFault,
    anchorEl,
    setAnchorEl,
    open,
    handleClose,
    handleClick,
    getFormattedDocumentPath,
    onClickCreateNewReceipt,
    openDeleteModal,
    onClickCloseDeleteModal,
    onClickOpenDeleteModal,
    onClickCancelReceipt,
    openCancelReceiptModal,
    onClickOpenCancelReceiptModal,
    onClickCloseCancelReceiptModal,
    showAddNewItemBtn,
    canEditDocument,
    isNewCreation,
    openMangerApprovalModal,
    onClickCloseMangerApprovalModal,
    onClickOpenMangerApprovalModal,
    getAllUsersCanApproveQuote,
    managersOptions,
    onClickSendAndChat,
    canAddDelivery,
    isInvoice,
    isProformaInvoice,
    isShowCloseButton,
    anchorElCloseBtn,
    handleCloseBtnClose,
    handleCloseBtnClick,
    openCloseBtn,
    onClickOpenCloseOrderModal,
    onClickCloseCloseOrderModal,
    handleDocumentNumberChange,
    documentCloseNumber,
    ManuallyCloseDocument,
    openCloseOrderModal,
    selectedDocumentId,
    handleAddExistingItem,
    isOrder,
    isQuote,
    openReceiptCreationModal,
    onClickCloseReceiptCreation,
    openExportReceiptPdfModal,
    onOpenExportReceiptPdfModal,
    onCloseExportReceiptPdfModal,
    handleClickPrint,
    openOrderNowPrePaymentModal,
    onClickCloseOrderNowPreModal,
    handleOrderConfirmation,
    setOpenReceiptCreationModal,
    isReceipt,
    canCancelReceipt,
    isDeliveryNote,
    AlertAddCustomerModal,
    isPurchaseInvoice,
    CheckoutOptionModal,
    onClickCloseCloseOrderNotesModal,
    onClickOpenCloseOrderNotesModal,
    openCloseOrderNotesModal,
    setOpenCloseOrderNotesModal,
    onClickCloseOrderCallback,
    isPurchaseOrder,
    handleClickPrintChecks,
    checkoutModalData,
    CheckoutModalOpen,
    setShowPrintJobsModal,
    handleProcessCheckout,
    handleGetGroupJobsUsingOrderId,
    isOpenGroupJobsModal,
    pendingOrderData,
    setPendingOrderData,
    ReceiptSummeryModal,
    filteredReceiptItems,
    finalTotalPayment,
    handleOpenReceiptSummary,
    sendBodyCommingFromOrder,
    navigateToJobsAfterOrderCreated,
    openSendPaymentModal,
    onClickCloseSendPaymentModal,
    onClickSendPayment,
    paymentModalTitle,
    isSendToClient,
    isSendOrderReadyToClient,
    packagingFlowContext: getDocumentFlowContext(),
    openPackagingModal,
    onClickOpenPackagingModal,
    onClickClosePackagingModal,
    navigateToPackagingPage,
    openShippingModal,
    onClickOpenShippingModal,
    onClickCloseShippingModal,
    navigateToShippingPage,
    openAddProductWithAIModal,
    onClickOpenAddProductWithAIModal: () => setOpenAddProductWithAIModal(true),
    onClickCloseAddProductWithAIModal: () =>
      setOpenAddProductWithAIModal(false),
  };
};

export { useButtonsContainer };
